import {
  IconArrowLeft,
  IconDotsVertical,
  IconMicrophone,
  IconMoodHappyFilled,
  IconPlus,
  IconSearch,
  IconSend2,
  IconStar,
  IconX,
  IconUsers,
  IconCheck,
  IconChecks,
  IconTrash,
  IconPlayerPlay,
  IconPlayerPause,
  IconMessageCircle2,
  IconDownload,
  IconExternalLink,
  IconFileText,
  IconFileZip,
  IconFileCode,
  IconFileDescription,
  IconVideo,
  IconPhoto,
} from "@tabler/icons-react";
import React, { useState, useEffect, useRef } from "react";
import { apiClient } from "../../../commonApis/apiClient";
import { useSelector } from "react-redux";
import { io, Socket } from "socket.io-client";
import Select from "react-select";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useLanguage, T } from "../../../context/LanguageContext";

interface Message {
  id: string;
  roomId: string;
  senderId: string;
  senderType: string;
  message?: string;
  fileUrl?: string;
  fileType?: string;
  voice?: boolean;
  duration?: string;
  readAt?: string | null;
  createdAt: string;
}

interface ChatRoom {
  id: string;
  type: "DIRECT" | "GROUP";
  name?: string;
  messages: Message[];
  participants: any[];
}

interface Contact {
  id: string;
  name: string;
  type: "SUPERADMIN" | "STAFF" | "ADMIN" | "LMCLIENT";
  clientId?: string;
}

export default function ChatSupportListTab() {
  const { t } = useLanguage();
  const [activeChats, setActiveChats] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const [groupName, setGroupName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedRoomRef = useRef<ChatRoom | null>(null);
  useEffect(() => {
    selectedRoomRef.current = selectedRoom;
  }, [selectedRoom]);

  const profile = useSelector((state: any) => state.auth.profile);
  const socketRef = useRef<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Multimedia states
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{ url: string; type: string; name?: string } | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 1. Fetch Active Chats and Contacts
  const fetchData = async () => {
    if (!profile?.id) {
      console.warn("ChatSupportListTab: profile.id is missing, skipping fetch");
      return;
    }
    console.log("ChatSupportListTab: Fetching data for profile.id:", profile.id);
    try {
      // 1. Fetch Active Chats
      const chatRes = await apiClient.get<any>(`/api/chat/my-chats/${profile.id}`);
      if (chatRes.success && (chatRes as any).rooms) {
        const rooms = (chatRes as any).rooms;
        setActiveChats(rooms);
        
        // Auto-select room from URL
        const chatId = searchParams.get('chatId');
        if (chatId) {
          const roomToSelect = rooms.find((r: any) => r.id === chatId);
          if (roomToSelect) {
            setSelectedRoom(roomToSelect);
            searchParams.delete('chatId');
            searchParams.delete('type');
            setSearchParams(searchParams, { replace: true });
          }
        }
      }

      // 2. Fetch Support Team (SuperAdmin & Staffs)
      const supportRes = await apiClient.get<any>("/api/chat/contacts/superadmin");
      
      const loadedContacts: Contact[] = [];

      // Add Support contacts
      if (supportRes.success && (supportRes as any).contacts) {
        const { superAdmins, staffs } = (supportRes as any).contacts;
        
        if (Array.isArray(superAdmins)) {
          superAdmins.forEach((sa: any) => {
            loadedContacts.push({ 
              id: sa.id, 
              name: sa.name || "Super Admin", 
              type: "SUPERADMIN" 
            });
          });
        }
        
        if (Array.isArray(staffs)) {
          staffs.forEach((staff: any) => {
            loadedContacts.push({ 
              id: staff.id, 
              name: staff.name || "Support Staff", 
              type: "STAFF" 
            });
          });
        }
      }

      console.log("ChatSupportListTab: Final contacts count:", loadedContacts.length);
      setContacts(loadedContacts);
    } catch (err) {
      console.error("ChatSupportListTab: Failed to fetch data", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [profile?.id]);

  // 2. Setup Socket Connection
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/api\/?$/, "") || "http://localhost:3002";
    socketRef.current = io(baseUrl, { transports: ["websocket"] });

    socketRef.current.on("connect", () => {
      console.log("Chat Socket Connected:", socketRef.current?.id);
      if (profile?.id) {
        socketRef.current?.emit("setup", { id: profile.id });
      }
    });

    socketRef.current.on("new-chat-message", (msg: Message) => {
      setMessages((prev) => {
        if (prev.some(m => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      
      // If we are in this room, mark it as read
      if (selectedRoomRef.current?.id === msg.roomId) {
        socketRef.current?.emit("mark-messages-read", { roomId: msg.roomId, userId: profile.id });
      }

      setTimeout(scrollToBottom, 100);
      fetchData(); // refresh sidebar list
    });

    socketRef.current.on("messages-read", (data: { roomId: string, userId: string, readAt: string }) => {
      console.log("📖 Received messages-read event:", data);
      if (selectedRoomRef.current?.id === data.roomId) {
        setMessages(prev => prev.map(m => {
          if (m.senderId !== data.userId && !m.readAt) {
            return { ...m, readAt: data.readAt };
          }
          return m;
        }));
      }
      fetchData();
    });

    socketRef.current.on("user-online", (userId: string) => {
      setOnlineUsers(prev => new Set([...Array.from(prev), userId]));
    });

    socketRef.current.on("user-offline", (userId: string) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    });

    socketRef.current.on("online-status-response", (statusMap: Record<string, boolean>) => {
      setOnlineUsers(prev => {
        const next = new Set(prev);
        Object.entries(statusMap).forEach(([id, isOnline]) => {
          if (isOnline) next.add(id);
          else next.delete(id);
        });
        return next;
      });
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [profile?.id]);

  // 3. Load Room Messages
  useEffect(() => {
    if (!selectedRoom) return;

    if ((selectedRoom as any).isDraft) {
      setMessages([]);
      return;
    }

    socketRef.current?.emit("join-chat-room", selectedRoom.id);
    socketRef.current?.emit("mark-messages-read", { roomId: selectedRoom.id, userId: profile.id });

    const loadMessages = async () => {
      try {
        const res = await apiClient.get<any>(`/api/chat/room/${selectedRoom.id}/messages`);
        if (res.success) {
          setMessages((res as any).messages || []);
          setTimeout(scrollToBottom, 100);
        }

        // Fetch online status for participants
        const participantIds = selectedRoom.participants.map(p => p.participantId);
        socketRef.current?.emit("get-online-status", participantIds);
      } catch (err) {
        console.error("Failed to load messages", err);
      }
    };
    loadMessages();
  }, [selectedRoom, profile?.id]);

  // 4. Modal Logic (contacts already fetched)
  useEffect(() => {
    if (isModalOpen) {
      // Contacts are already fetched in fetchData
    }
  }, [isModalOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (multimedia?: { fileUrl: string; fileType: string; voice?: boolean; duration?: string }) => {
    if ((!newMessage.trim() && !multimedia) || !selectedRoom || !profile?.id) return;

    let roomId = selectedRoom.id;
    // If it's a draft room, create it now
    if ((selectedRoom as any).isDraft) {
      try {
        const participant = selectedRoom.participants[0];
        const res = await apiClient.post<any>("/api/chat/direct", {
          participantAId: profile.id,
          participantAType: "ADMIN",
          participantBId: participant.participantId,
          participantBType: participant.participantType,
          clientId: profile.id,
        });
        if (res.success && (res as any).room) {
          const newRoom = (res as any).room;
          roomId = newRoom.id;
          setSelectedRoom(newRoom);
          // Replace draft with actual room
          setActiveChats(prev => [newRoom, ...prev.filter(r => r.id !== selectedRoom.id)]);
        } else {
          return;
        }
      } catch (err) {
        console.error("Failed to create room on first message", err);
        return;
      }
    }

    const payload = {
      roomId,
      senderId: profile.id,
      senderType: "ADMIN",
      message: newMessage.trim(),
      ...multimedia
    };

    socketRef.current?.emit("send-chat-message", payload);
    setNewMessage("");
    setShowEmojiPicker(false);
  };

  const handleDeleteChat = async (e: React.MouseEvent, roomId: string) => {
    e.stopPropagation();
    if (!window.confirm(t("Are you sure you want to delete this chat?"))) return;
    try {
      await apiClient.delete<any>(`/api/chat/room/${roomId}`);
      setActiveChats((prev) => prev.filter((r) => r.id !== roomId));
      if (selectedRoom?.id === roomId) {
        setSelectedRoom(null);
        setMessages([]);
      }
    } catch (err) {
      console.error("Failed to delete chat", err);
    }
  };

  // ── Multimedia Handlers ───────────────────────────────────────────────────
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRoom) return;

    // Check file size (2 MB limit)
    const MAX_SIZE = 2 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      toast.error(t('File size exceeds 2MB limit'));
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await apiClient.post<any>('/api/chat/upload', formData, true);
      if (res.success) {
        handleSendMessage({
          fileUrl: (res as any).fileUrl,
          fileType: (res as any).fileType
        });
      }
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const durationStr = formatTime(recordingTime);
        
        const formData = new FormData();
        formData.append('file', audioBlob, 'voice-message.wav');

        setIsUploading(true);
        try {
          const res = await apiClient.post<any>('/api/chat/upload', formData, true);
          if (res.success) {
            handleSendMessage({
              fileUrl: (res as any).fileUrl,
              fileType: 'audio/wav',
              voice: true,
              duration: durationStr
            });
          }
        } catch (err) {
          console.error('Voice upload failed:', err);
        } finally {
          setIsUploading(false);
        }
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone access denied:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const addEmoji = (emoji: string) => {
    setNewMessage(prev => prev + emoji);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSendMessage();
  };

  const handleCreateChat = async () => {
    if (selectedContacts.length === 0 || !profile?.id) return;

    if (selectedContacts.length === 1) {
      // Direct: Check if we already have an active chat with this contact
      const contact = selectedContacts[0];
      const existing = activeChats.find(r => 
        r.type === "DIRECT" && 
        r.participants.some(p => p.participantId === contact.id)
      );

      if (existing) {
        setSelectedRoom(existing);
        setIsModalOpen(false);
        setSelectedContacts([]);
        return;
      }

      // Lazy Creation: Create a draft room in UI
      const draftRoom: any = {
        id: `draft-${contact.id}`,
        isDraft: true,
        type: "DIRECT",
        participants: [{
          participantId: contact.id,
          participantType: contact.type,
          name: contact.name
        }],
        messages: []
      };
      setSelectedRoom(draftRoom);
      setActiveChats(prev => [draftRoom, ...prev.filter(r => r.id !== draftRoom.id)]);
      setIsModalOpen(false);
      setSelectedContacts([]);
      return;
    } else {
      // Group
      if (!groupName.trim()) return alert(t("Please enter a group name"));
      const participants = selectedContacts.map((c) => ({ id: c.id, type: c.type }));
      participants.push({ id: profile.id, type: "ADMIN" });

      try {
        const res = await apiClient.post<any>("/api/chat/group", {
          name: groupName,
          participants,
          clientId: profile.id,
        });
        if (res.success && (res as any).room) {
          setSelectedRoom((res as any).room);
          setIsModalOpen(false);
          setSelectedContacts([]);
          setGroupName("");
          fetchData();
        }
      } catch (err) {
        console.error("Failed to create group chat", err);
      }
    }
  };

  const toggleContactSelection = (contact: Contact) => {
    if (selectedContacts.some((c) => c.id === contact.id)) {
      setSelectedContacts(selectedContacts.filter((c) => c.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  // Helper
  const getRoomName = (room: ChatRoom) => {
    if (!room) return t("Unknown Chat");
    if (room.type === "GROUP") return room.name || t("Group Chat");
    
    // For direct chat, find the other participant's name
    if (room.participants && Array.isArray(room.participants)) {
      const otherParticipant = room.participants.find(p => String(p.participantId) !== String(profile?.id));
      if (otherParticipant) {
        if (otherParticipant.name && otherParticipant.name !== "Unknown") return otherParticipant.name;
        const contact = contacts.find(c => String(c.id) === String(otherParticipant.participantId));
        if (contact) return contact.name;
      }
    }
    
    return t("Direct Chat");
  };

  const renderAvatar = (room: ChatRoom, size: string = "w-10 h-10") => {
    if (room.type === "GROUP") {
      return (
        <div className={`${size} rounded-lg flex-shrink-0 flex items-center justify-center bg-gray-200 overflow-hidden`}>
          <IconUsers size={20} className="text-gray-600" />
        </div>
      );
    }

    const otherPart = room.participants?.find((p: any) => String(p.participantId) !== String(profile?.id));
    const name = getRoomName(room);
    const photo = otherPart?.photo;

    return (
      <div className={`${size} rounded-lg flex-shrink-0 relative`}>
        <div className="w-full h-full rounded-lg flex items-center justify-center bg-yellow-400 text-black font-bold overflow-hidden">
          {photo ? (
            <img src={photo} className="w-full h-full object-cover" alt={name} />
          ) : (
            <span>{name.charAt(0).toUpperCase()}</span>
          )}
        </div>
        {otherPart && onlineUsers.has(String(otherPart.participantId)) && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-white rounded-full z-10 shadow-sm" />
        )}
      </div>
    );
  };

  const renderFilePreviewModal = () => {
    if (!previewFile) return null;

    const isImage = previewFile.type?.startsWith("image/");
    const isVideo = previewFile.type?.startsWith("video/");
    const isPdf = previewFile.type === "application/pdf";

    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4 md:p-10 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="absolute top-6 right-6 flex items-center gap-4">
          <a
            href={previewFile.url}
            download
            className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
            title="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <IconDownload size={24} />
          </a>
          <button
            onClick={() => setPreviewFile(null)}
            className="p-2.5 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors backdrop-blur-md"
            title="Close"
          >
            <IconX size={24} />
          </button>
        </div>

        <div className="w-full h-full flex items-center justify-center pointer-events-none" onClick={() => setPreviewFile(null)}>
          <div className="max-w-full max-h-full flex items-center justify-center pointer-events-auto" onClick={(e) => e.stopPropagation()}>
            {isImage ? (
              <div className="relative group cursor-zoom-in">
                <img
                  src={previewFile.url}
                  alt="Preview"
                  className="max-w-full max-h-[85vh] rounded-lg shadow-2xl object-contain transition-transform duration-300 group-active:scale-150"
                />
              </div>
            ) : isVideo ? (
              <video
                src={previewFile.url}
                controls
                autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
            ) : isPdf ? (
              <iframe
                src={previewFile.url}
                className="w-[90vw] h-[85vh] rounded-lg bg-white shadow-2xl border-none"
                title="PDF Preview"
              />
            ) : (
              <div className="bg-white p-12 rounded-3xl flex flex-col items-center gap-6 shadow-2xl text-center min-w-[320px]">
                <div className="w-24 h-24 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-400">
                  <IconFileDescription size={56} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1"><T>File Preview Unavailable</T></h3>
                  <p className="text-sm text-gray-500"><T>You can still download the file to view it.</T></p>
                </div>
                <a
                  href={previewFile.url}
                  download
                  className="w-full py-3.5 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <IconDownload size={20} />
                  <T>Download File</T>
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getRoleLabel = (type: string) => {
    switch (type) {
      case "ADMIN": return t("Admin");
      case "LMCLIENT": return t("LM Staff");
      case "SUPERADMIN": return t("Super Admin");
      case "STAFF": return t("Staff");
      default: return t(type);
    }
  };

  return (
    <div className="flex h-[calc(100vh-12rem)] overflow-hidden bg-[#F7F6F2] rounded-[20px] relative">
      {/* Sidebar */}
      <div className={`flex-col w-full md:w-80 border-r border-[#d2d2d2] ${selectedRoom ? "hidden md:flex" : "flex"}`}>
        <div className="flex items-center justify-between px-4 py-6 border-b border-[#d2d2d2]">
          <h2 className="text-lg font-semibold"><T>Chats</T></h2>
          <div className="flex gap-2">
            <button onClick={() => setIsModalOpen(true)} className="p-1 bg-yellow-400 rounded-full text-black hover:bg-yellow-500" title={t("New Chat")}>
              <IconPlus size={20} stroke={2} />
            </button>
            <IconDotsVertical stroke={1.5} size={22} className="text-gray-600" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-[#d2d2d2]">
          {(() => {
            const list = [...activeChats];
            if (selectedRoom && (selectedRoom as any).isDraft && !list.some(r => r.id === selectedRoom.id)) {
              list.push(selectedRoom);
            }
            return list.map((room) => (
            <div
              key={room.id}
              onClick={() => setSelectedRoom(room)}
              className={`flex justify-between items-start px-4 py-4 hover:bg-black/5 cursor-pointer space-x-4 rtl:space-x-reverse ${selectedRoom?.id === room.id ? "bg-black/5" : ""}`}
            >
              <div className="flex items-center space-x-3 rtl:space-x-reverse w-full">
                {renderAvatar(room)}
                <div className="space-y-1 w-full overflow-hidden">
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm truncate">{getRoomName(room)}</div>
                    <button 
                      onClick={(e) => handleDeleteChat(e, room.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title={t("Delete Chat")}
                    >
                      <IconTrash size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-black/60 truncate">
                    {room.messages && room.messages.length > 0 ? room.messages[room.messages.length - 1].message : t("No messages yet")}
                  </div>
                </div>
              </div>
            </div>
          ));
        })()}
          {activeChats.length === 0 && !selectedRoom && <div className="text-center text-sm text-gray-500 py-4"><T>No active chats</T></div>}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!selectedRoom && "hidden md:flex"}`}>
        {selectedRoom ? (
          <>
            <div className="flex items-center justify-between py-[18px] px-4 border-b border-[#d2d2d2]">
              <div className="flex items-center space-x-3 rtl:space-x-reverse">
                <button onClick={() => setSelectedRoom(null)} className="md:hidden">
                  <IconArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                {renderAvatar(selectedRoom)}
                <div className="flex flex-col">
                  <span className="font-medium">{getRoomName(selectedRoom)}</span>
                  <span className="text-xs text-gray-500">{selectedRoom.type === "GROUP" ? t("Group Chat") : t("Direct Chat")}</span>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-4 rtl:space-x-reverse">
                <IconStar className="w-5 h-5 text-[#FBD037]" />
                <IconSearch className="w-5 h-5 text-gray-600" />
                <IconDotsVertical stroke={3} className="w-5 h-5 text-gray-600" />
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 p-4 md:p-6 overflow-y-auto space-y-6">
              <div className="text-xs text-gray-400 text-center"><T>Chat started</T></div>
              {messages.map((msg) => {
                const isMe = msg.senderId === profile?.id;
                return (
                  <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-xs md:max-w-md px-4 py-3 text-sm whitespace-pre-line ${
                        isMe ? "bg-[#212121] text-white rounded-l-3xl rounded-t-3xl shadow-md" : "bg-white text-[#212121] rounded-r-3xl rounded-t-3xl border border-[#d2d2d2] shadow-sm"
                      }`}
                    >
                      {selectedRoom.type === "GROUP" && !isMe && (
                        <div className="text-[10px] font-bold mb-1 opacity-60 uppercase tracking-widest">{msg.senderType}</div>
                      )}
                      {msg.message && <p className="leading-relaxed">{msg.message}</p>}

                      {msg.fileUrl && !msg.voice && (
                        <div className="mt-2">
                          {msg.fileType?.startsWith("image/") ? (
                            <div 
                              className="relative group cursor-pointer overflow-hidden rounded-xl"
                              onClick={() => setPreviewFile({ url: msg.fileUrl!, type: msg.fileType! })}
                            >
                              <img
                                src={msg.fileUrl}
                                alt={t("attachment")}
                                className="max-w-full h-auto border border-white/10 transition-transform duration-300 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <IconSearch size={24} className="text-white" />
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() => setPreviewFile({ url: msg.fileUrl!, type: msg.fileType! })}
                              className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                isMe ? "bg-white/10 border-white/20 hover:bg-white/20" : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                              } no-underline`}
                            >
                              <div className={isMe ? "text-[#FBD037]" : "text-gray-600"}>
                                {msg.fileType?.includes("zip") ? <IconFileZip size={20} /> : <IconFileDescription size={20} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-xs font-semibold truncate ${isMe ? "text-white" : "text-[#212121]"}`}>{t("Attachment")}</p>
                                <p className={`text-[10px] opacity-60 ${isMe ? "text-white/60" : "text-[#212121]/60"}`}>{t("Click to preview")}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {msg.voice && msg.fileUrl && (
                        <div className={`mt-2 flex items-center gap-3 p-3 rounded-2xl ${isMe ? 'bg-white/10' : 'bg-gray-50 border border-gray-100'}`}>
                          <button 
                            onClick={(e) => {
                              const audio = e.currentTarget.parentElement?.querySelector('audio');
                              const iconDiv = e.currentTarget.querySelector('.audio-icon-wrapper');
                              if (audio) {
                                if (audio.paused) {
                                  document.querySelectorAll('audio').forEach(a => { if(a !== audio) a.pause() });
                                  audio.play();
                                  if (iconDiv) iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-player-pause" width="20" height="20" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 5m0 1a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" /><path d="M14 5m0 1a1 1 0 0 1 1 -1h2a1 1 0 0 1 1 1v12a1 1 0 0 1 -1 1h-2a1 1 0 0 1 -1 -1z" /></svg>';
                                } else {
                                  audio.pause();
                                  if (iconDiv) iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-player-play" width="20" height="20" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 4v16l13 -8z" /></svg>';
                                }
                              }
                            }}
                            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${isMe ? 'bg-[#FBD037] text-black' : 'bg-[#212121] text-white shadow-sm'} hover:scale-105 active:scale-95`}
                          >
                            <div className="audio-icon-wrapper flex items-center justify-center">
                              <IconPlayerPlay size={20} />
                            </div>
                          </button>
                          <div className="flex-1">
                            <div className={`h-1.5 rounded-full ${isMe ? 'bg-white/20' : 'bg-gray-200'} relative overflow-hidden`}>
                              <div className="absolute left-0 top-0 h-full w-0 bg-[#FBD037] rounded-full transition-all duration-300 audio-progress" />
                            </div>
                            <div className="flex justify-between mt-1.5">
                              <span className="text-[10px] font-medium opacity-60 italic">{t("Voice Clip")}</span>
                              <span className="text-[10px] font-bold">{msg.duration || '0:00'}</span>
                            </div>
                          </div>
                          <audio 
                            src={msg.fileUrl} 
                            className="hidden" 
                            onTimeUpdate={(e) => {
                              const audio = e.currentTarget;
                              const progress = audio.parentElement?.querySelector('.audio-progress') as HTMLElement;
                              if (progress) {
                                const percent = (audio.currentTime / audio.duration) * 100;
                                progress.style.width = `${percent}%`;
                              }
                            }}
                            onEnded={(e) => {
                              const progress = e.currentTarget.parentElement?.querySelector('.audio-progress') as HTMLElement;
                              if (progress) progress.style.width = '0%';
                              const iconDiv = e.currentTarget.parentElement?.querySelector('.audio-icon-wrapper') as HTMLElement;
                              if (iconDiv) iconDiv.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-tabler icon-tabler-player-play" width="20" height="20" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 4v16l13 -8z" /></svg>';
                            }}
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-end gap-1 mt-2">
                        <span className="text-[10px] opacity-40 font-medium">
                          {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {isMe && (
                          <span className={`${msg.readAt ? "text-blue-400" : "text-gray-400"} opacity-80`}>
                            {msg.readAt ? <IconChecks size={14} stroke={3} /> : <IconCheck size={14} stroke={3} />}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {renderFilePreviewModal()}

            <div className="relative flex items-center gap-4 p-4 border-t border-[#d2d2d2] bg-white">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileUpload}
              />
              
              {showEmojiPicker && (
                <div className="absolute bottom-full left-4 mb-4 p-3 bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-[#d2d2d2] grid grid-cols-6 gap-2 z-[100] animate-in fade-in slide-in-from-bottom-4">
                  {['😊', '😂', '😍', '👍', '🙏', '🔥', '👏', '🙌', '⭐', '📍', '✅', '❌', '🚀', '📦', '🚚', '💡', '📞', '💬'].map(emoji => (
                    <button
                      key={emoji}
                      onClick={() => addEmoji(emoji)}
                      className="w-10 h-10 flex items-center justify-center hover:bg-yellow-50 rounded-xl text-xl transition-all hover:scale-110 active:scale-95"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}

              <button 
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all flex-shrink-0 ${showEmojiPicker ? 'bg-[#212121] text-yellow-400' : 'text-gray-400 hover:bg-gray-50'}`}
              >
                <IconMoodHappyFilled size={24} />
              </button>

              <div className="relative flex items-center w-full">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="absolute left-2 bg-yellow-400 w-8 h-8 rounded-full flex items-center justify-center text-black hover:bg-yellow-500 transition-colors shadow-sm disabled:opacity-50"
                >
                  {isUploading ? (
                    <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                  ) : (
                    <IconPlus stroke={1.5} size={22} />
                  )}
                </button>
                
                {isRecording ? (
                  <div className="flex-1 flex items-center gap-4 pl-12 pr-4 py-2.5 rounded-full bg-red-50 border border-red-100">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
                    <span className="flex-1 text-sm font-bold text-red-600 tracking-wide">{t("RECORDING...")} {formatTime(recordingTime)}</span>
                    <button onClick={stopRecording} className="text-[11px] font-black text-red-700 uppercase tracking-widest hover:underline px-2 py-1 bg-white rounded-lg shadow-sm border border-red-100">{t("Stop")}</button>
                  </div>
                ) : (
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t("Write a message...")}
                    className="flex-1 pl-12 pr-4 py-3 w-full bg-[#F7F6F2] rounded-2xl border border-transparent outline-none text-sm focus:bg-white focus:border-yellow-400 transition-all placeholder:text-gray-400"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                )}
              </div>

              {!isRecording && (
                <button 
                  onClick={startRecording}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:bg-gray-50 hover:text-red-500 transition-all flex-shrink-0"
                >
                  <IconMicrophone className="w-5 h-5" />
                </button>
              )}

              <button
                type="button"
                onClick={() => handleSendMessage()}
                disabled={(!newMessage.trim() && !isRecording) || isUploading}
                className="bg-yellow-400 p-3 rounded-2xl flex-shrink-0 hover:bg-yellow-500 transition-all shadow-md active:scale-95 disabled:opacity-30 disabled:shadow-none"
              >
                <IconSend2 className="w-5 h-5 text-[#000]" />
              </button>
            </div>
          </>
        ) : (
          <div className="hidden md:flex flex-1 flex-col justify-center items-center bg-gray-50/50 p-8 text-center animate-in fade-in duration-500">
            <div className="w-32 h-32 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 mb-6 relative">
              <div className="absolute inset-0 bg-yellow-400 rounded-full blur-2xl opacity-10 animate-pulse" />
              <IconMessageCircle2 size={64} className="text-[#FBD037]" stroke={1.5} />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2"><T>Your Conversations</T></h2>
            <p className="text-sm text-gray-500 max-w-sm leading-relaxed">
              <T>Select a contact from the sidebar or click the new chat button to start messaging with your team.</T>
            </p>
          </div>
        )}
      </div>

      {/* Create Chat Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-lg"><T>New Chat</T></h3>
              <button onClick={() => { setIsModalOpen(false); setSelectedContacts([]); setGroupName(""); }} className="text-gray-500 hover:bg-gray-100 p-1 rounded-full">
                <IconX size={20} />
              </button>
            </div>
            
            {selectedContacts.length > 1 && (
              <div className="p-4 border-b">
                <input
                  type="text"
                  placeholder={t("Enter Group Name")}
                  className="w-full px-3 py-2 border rounded-lg outline-none focus:border-yellow-400"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </div>
            )}

            <div className="p-4 flex-1 overflow-y-auto max-h-[400px]">
              <label className="block text-sm font-medium text-gray-700 mb-2"><T>Select Participants</T></label>
              <Select
                isMulti
                options={contacts.map(c => ({ value: c.id, label: `${c.name} (${getRoleLabel(c.type)})`, data: c }))}
                onChange={(selected: any) => {
                  setSelectedContacts(selected ? selected.map((s: any) => s.data) : []);
                }}
                placeholder={t("Search and select users...")}
                className="text-sm"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  control: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    borderColor: '#D8D8D8',
                    '&:hover': { borderColor: '#FBD037' }
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: '#FEF9C3',
                    borderRadius: '4px',
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: '#854D0E',
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: '#854D0E',
                    '&:hover': { backgroundColor: '#FDE047', color: '#000' }
                  })
                }}
              />
              
              {contacts.length === 0 && (
                <div className="text-center text-sm text-gray-500 mt-4">{t("No contacts found")}</div>
              )}
            </div>

            <div className="p-4 border-t flex justify-end">
              <button
                onClick={handleCreateChat}
                disabled={selectedContacts.length === 0}
                className="bg-yellow-400 px-6 py-2 rounded-lg font-medium disabled:opacity-50"
              >
                {selectedContacts.length > 1 ? t("Create Group") : t("Start Chat")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
